<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex xs12 mt-3>
          <h1 class="headline">{{ $t('bookingsList.title') }}</h1>
        </v-flex>
        <!-- Error Alert -->
        <alert-error class="px-3 mt-4" />
        <!-- Success Alert-->
        <v-layout
          v-if="successAlert.state"
          id="sucessAlert"
          row
          class="alert-wrapper mt-2 mb-2"
        >
          <v-flex sm12>
            <v-alert :value="successAlert.state" type="success">
              {{ successAlert.message }}
            </v-alert>
          </v-flex>
        </v-layout>
      </v-layout>
      <v-layout wrap>
        <v-flex xs12 sm12 md4>
          <v-layout wrap align-center>
            <v-flex xs12 mt-4 pt-4>
              <v-menu
                ref="menuDate"
                :close-on-content-click="false"
                v-model="menuDate"
                :return-value.sync="bookingDate"
                max-width="280px"
                min-width="280px"
                class="pr-4 mt-2"
                transition="scale-transition"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    v-model="bookingDate"
                    :label="$t('common.fields.bookingDate')"
                    readonly
                    class="date"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="bookingDate"
                  color="primary lighten-1"
                  :locale="$i18n.locale"
                  width="280"
                  @input="changeDate"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12>
              <v-layout wrap align-center>
                <v-flex xs12 v-for="type in bookingsTypes" :key="type.id">
                  <v-checkbox
                    :label="type.title"
                    :input-value="type.value"
                    color="primary"
                    class="mt-0"
                    @change="chooseBookingType(type.id)"
                  ></v-checkbox>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs12 sm12 md8 mt-4>
          <v-layout wrap align-center>
            <v-flex xs12 sm12 mt-4>
              <v-layout wrap align-center class="search-wrapper">
                <v-text-field
                  class="search"
                  ref="search"
                  v-model="search"
                  :placeholder="$t('bookingsList.search.placeholder')"
                  autocomplete="off"
                  solo
                ></v-text-field>
                <v-btn
                  type="button"
                  class="search-btn warning mb-4"
                  depressed
                  large
                  @click="searchBookings"
                >
                  {{ $t('common.btns.search') }}
                </v-btn>
              </v-layout>
            </v-flex>
            <v-flex xs12 sm12 class="filter-wrapper">
              <v-select
                :items="bookingsStatuses"
                v-model="bookingStatus"
                :label="$t('common.fields.status')"
                item-text="title"
                item-value="id"
                class="filter"
                @change="changeBookingsStatus"
                return-object
              ></v-select>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex xs12>
      <v-data-table
        :items="bookingsList.items"
        :headers="headers"
        :loading="isLoading"
        :loading-text="$t('common.table.dataIsLoading')"
        :server-items-length="2"
        :mobile-breakpoint="100"
        class="table-custom bookingsList small mb-4"
        hide-default-footer
      >
        <template slot="item" slot-scope="{ item }">
          <tr :key="item.id" :class="{ canceled: item.statusId === 2 }">
            <td class="text-xs-left">{{ item.num }}</td>
            <td class="text-xs-left">
              <v-layout align-center justify-start>
                {{ item.id }}
              </v-layout>
            </td>
            <td class="text-xs-left">{{ item.number }}</td>
            <td class="text-xs-left">{{ item.brand }}</td>
            <td class="text-xs-left">{{ item.model }}</td>
            <td class="text-xs-left">
              <div>{{ item.date }}</div>
              <div>{{ item.type }}</div>
            </td>
            <td class="text-xs-left">{{ item.name }}</td>
            <td class="text-xs-left company">
              <div>{{ item.phone }}</div>
              <div>{{ item.secondPhone }}</div>
            </td>
            <td
              :class="{ 'green--text': item.statusId === 1 }"
              class="text-xs-left"
            >
              {{ item.status }}
            </td>
            <td class="text-xs-left">
              <div v-if="item.statusId === 2">
                {{ item.canceledBy }}
              </div>
              <a
                v-if="item.statusId === 1"
                class="font-weight-bold py-1"
                @click.stop="cancelBooking(item.id)"
              >
                {{ $t('common.btns.cancelBooking') }}
              </a>
            </td>
          </tr>
        </template>
        <template slot="no-data">{{ $t('common.table.noData') }}</template>
      </v-data-table>
      <div v-if="paginationPage" class="text-xs-center">
        <v-pagination
          :value="paginationPage"
          :length="paginationLength"
          @input="choosePaginatorPage($event)"
        ></v-pagination>
      </div>
    </v-flex>
    <!-- Cancel Booking Dialog -->
    <cancel-booking-dialog @getBookingsList="searchBookings" />
  </v-layout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import CancelBookingDialog from '@/components/booking/CancelBookingDialog.vue';
import AlertError from '@/components/shared/AlertError.vue';

export default {
  name: 'BookingsListPage',
  components: {
    CancelBookingDialog,
    AlertError,
  },
  data() {
    return {
      search: '',
      headers: [
        { text: this.$t('common.number'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.id'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.number'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.brand'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.model'), sortable: false },
        {
          text: this.$t('bookingsList.tableHeaders.dateAndShift'),
          sortable: false,
        },
        { text: this.$t('bookingsList.tableHeaders.name'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.phone'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.status'), sortable: false },
        { text: this.$t('bookingsList.tableHeaders.action'), sortable: false },
      ],
      defaultStatus: {
        id: '',
        title: this.$t('bookings.allBookings'),
      },
      bookingStatus: {
        id: '',
        title: this.$t('bookings.allBookings'),
      },
      bookingDate: new Date().toISOString().substr(0, 10),
      menuDate: false,
    };
  },
  watch: {
    /* eslint-disable */
    '$i18n.locale': async function() {
      await this.fetchBookingsProperties();
      await this.fetchBookings({
        page: this.paginationPage,
        searchString: this.search,
        bookingStatus: this.bookingStatus.id.toString(),
        bookingDate: this.bookingDate,
        bookingType: this.bookingsTypes
          .filter((item) => !!item.value)
          .map((item) => item.id)
          .toString(),
      });
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'global/getLoader',
      bookingsList: 'booking/getBookingsListData',
      statuses: 'booking/getBookingsStatuses',
      types: 'booking/getBookingsTypes',
      successAlert: 'global/getAlertSuccess',
    }),
    bookingsStatuses() {
      return [this.defaultStatus].concat(this.statuses);
    },
    bookingsTypes() {
      const bookingsTypesArr = [];
      this.types.forEach((booking) => {
        const item = {
          id: booking.id,
          title: booking.title,
          value: true,
        };
        bookingsTypesArr.push(item);
      });
      return bookingsTypesArr;
    },
    paginationLength() {
      return this.bookingsList.lastPage;
    },
    paginationPage() {
      if (
        this.bookingsList.currentPage === 1 &&
        this.bookingsList.lastPage === 1
      ) {
        return 0;
      }
      return this.bookingsList.currentPage;
    },
  },
  async created() {
    await this.fetchBookingsProperties();
    await this.fetchBookings({
      page: 1,
      searchString: this.search,
      bookingStatus: this.bookingStatus.id.toString(),
      bookingDate: this.bookingDate,
      bookingType: this.bookingsTypes
        .filter((item) => !!item.value)
        .map((item) => item.id)
        .toString(),
    });
  },
  destroyed() {
    this.setBookingsListData({});
    this.setStatuses([]);
    this.setTypes([]);
    this.clearAlertError();
    this.clearAlertSuccess();
  },
  methods: {
    ...mapActions({
      fetchBookings: 'booking/fetchBookingsListData',
      fetchBookingsProperties: 'booking/fetchBookingsProperties',
      precancelBooking: 'booking/cancelBooking',
    }),
    ...mapMutations({
      setBookingsListData: 'booking/setBookingsListData',
      setStatuses: 'booking/setBookingsStatuses',
      setTypes: 'booking/setBookingsTypes',
      setCancelBookingId: 'booking/setCancelBookingId',
      setCancelBookingDialogState: 'booking/setCancelBookingDialogState',
      clearAlertError: 'global/clearAlertError',
      clearAlertSuccess: 'global/clearAlertSuccess',
    }),
    async searchBookings() {
      await this.fetchBookings({
        page: 1,
        searchString: this.search,
        bookingStatus: this.bookingStatus.id.toString(),
        bookingDate: this.bookingDate,
        bookingType: this.bookingsTypes
          .filter((item) => !!item.value)
          .map((item) => item.id)
          .toString(),
      });
    },
    async choosePaginatorPage(page) {
      await this.fetchIBookings({
        page,
        searchString: this.search,
        bookingStatus: this.bookingStatus.id.toString(),
        bookingDate: this.bookingDate,
        bookingType: this.bookingsTypes
          .filter((item) => !!item.value)
          .map((item) => item.id)
          .toString(),
      });
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
    async changeBookingsStatus() {
      await this.searchBookings();
    },
    async changeDate() {
      this.$refs.menuDate.save(this.bookingDate);
      await this.searchBookings();
    },
    async chooseBookingType(typeId) {
      const position = this.bookingsTypes
        .map((item) => item.id)
        .indexOf(typeId);
      this.bookingsTypes[position].value = !this.bookingsTypes[position].value;
      await this.searchBookings();
    },
    async cancelBooking(bookingId) {
      this.setCancelBookingId(bookingId);
      this.setCancelBookingDialogState(true);
      try {
        await this.precancelBooking(bookingId);
        this.clearAlertError();
      } catch (error) {
        this.setCancelBookingId(null);
        this.setCancelBookingDialogState(false);
        this.$vuetify.goTo(0, 'easeInOutCubic');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-wrapper {
  .search {
    margin-right: -12px;
    margin-bottom: -12px;
    max-width: 480px;

    @media (max-width: 480px) {
      margin-right: 0px;
      margin-bottom: -12px;
      max-width: 100%;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;

    .search {
      width: 100%;
    }
  }
}
.date {
  max-width: 200px;
}
.canceled {
  background: rgba(233, 30, 30, 0.25);
}
</style>
